import { useCallback, useEffect, useRef, useState } from 'react';
import useAuthenticatedApi from '../../Authentication/components/hooks/useAuthenticatedApi';
import { LIVE_WINDOW_MS } from '../chrome/useEventChrome';
import { SANDBOX_DATA } from './sandbox';
import { CalEvent, CalStatus, addDays, dayStart, endDayOf, shortTime } from '../components/calendar';

/* Read-only data for the schedule views: one ranged list call plus per-event detail. */

export interface ProdRow {
    activationId: number;
    name: string;
    isProvisional?: boolean;
    isArchived?: boolean;
    jobId?: number | null;
    jobName?: string | null;
    status?: string;
    startAt?: string | null;
    endAt?: string | null;
    venueName?: string | null;
    staffCount?: number;
}

export interface EventInfo {
    prod?: any;
    devices?: any[];
    setup?: any;
    stats?: any;
}

export type DetailKind = keyof EventInfo;

const iso = (t: number) => new Date(t).toISOString();

/** Production rows whose start falls in [from, to] (local days, inclusive). */
export function useProductionRange(from: number | null, to: number | null, enabled = true) {
    const api = useAuthenticatedApi();
    const [rows, setRows] = useState<ProdRow[] | null>(null);
    const [failed, setFailed] = useState(false);
    /* only the latest request may land (range changes and reloads race) */
    const latest = useRef(0);
    const load = useCallback(() => {
        const req = ++latest.current;
        const alive = () => req === latest.current;
        if (!enabled) return;
        const params =
            from !== null && to !== null ? { dateFrom: iso(from), dateTo: iso(addDays(to, 1) - 1) } : undefined;
        api.get('/production/activations', { params })
            .then((r: any) => {
                if (!alive()) return;
                setRows(Array.isArray(r.data) ? r.data : []);
                setFailed(false);
            })
            .catch(() => alive() && setFailed(true));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [from, to, enabled]);
    useEffect(load, [load]);
    return { rows, failed, reload: load };
}

const URL_FOR: Record<DetailKind, (id: number) => string> = {
    prod: (id) => `/production/activations/${id}`,
    devices: (id) => `/activations/${id}/devices`,
    setup: (id) => `/dev/capture-setup/${id}`,
    stats: (id) => `/activations/${id}/photoStats`,
};

/** Per-event detail, fetched once per id and kept as ids come and go. */
export function useEventDetails(ids: number[], kinds: DetailKind[]) {
    const api = useAuthenticatedApi();
    const [info, setInfo] = useState<Record<number, EventInfo>>({});
    const asked = useRef(new Set<number>());
    const key = `${kinds.join('+')}|${ids.join(',')}`;
    useEffect(() => {
        const need = ids.filter((id) => !asked.current.has(id));
        /* capture setup is sandbox-only (no backend yet) */
        const want = kinds.filter((k) => k !== 'setup' || SANDBOX_DATA);
        if (need.length === 0) return;
        need.forEach((id) => asked.current.add(id));
        /* each event lands on its own; a failed one is asked again next time */
        need.forEach(async (id) => {
            let failed = false;
            const parts = await Promise.all(
                want.map((k) =>
                    api
                        .get(URL_FOR[k](id))
                        .then((r: any) => [k, r.data] as const)
                        .catch(() => {
                            failed = true;
                            return [k, undefined] as const;
                        }),
                ),
            );
            if (failed) asked.current.delete(id);
            setInfo((cur) => ({ ...cur, [id]: Object.fromEntries(parts) as EventInfo }));
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [key]);
    return info;
}

export const isLiveStats = (stats?: any) => {
    const t = stats?.lastPhoto?.createdAt;
    return !!t && Date.now() - new Date(t).getTime() < LIVE_WINDOW_MS;
};

function ago(t: string) {
    const ms = Date.now() - new Date(t).getTime();
    if (ms < 90 * 1000) return 'just now';
    if (ms < 90 * 60 * 1000) return `${Math.round(ms / 60000)}m ago`;
    return `${Math.round(ms / 3600000)}h ago`;
}

/** Status dot + note for one event: live, ready, or needs attention. */
export function readinessOf(info: EventInfo | undefined, endDay: number, live?: string | false) {
    const today = dayStart(Date.now());
    if (live) return { status: 'live' as CalStatus, note: live };
    if (info?.stats && isLiveStats(info.stats))
        return { status: 'live' as CalStatus, note: `Live · ${ago(info.stats.lastPhoto.createdAt)}` };
    if (endDay < today || !info || !Array.isArray(info.devices)) return { status: 'unknown' as CalStatus, note: undefined };
    const n = info.devices.length;
    if (n === 0) return { status: 'attention' as CalStatus, note: 'No devices assigned' };
    if (info.setup?.app === 'clickerbooth' && !info.setup.experiencePublished)
        return { status: 'attention' as CalStatus, note: 'Experience not published' };
    return { status: 'ready' as CalStatus, note: `Ready · ${n} ${n === 1 ? 'device' : 'devices'}` };
}

/** A production row (plus detail) as a calendar event. */
export function prodToEvent(r: ProdRow, info?: EventInfo, location?: string): CalEvent | null {
    if (!r.startAt) return null;
    const startDay = dayStart(r.startAt);
    const endDay = r.endAt ? endDayOf(r.endAt, startDay) : startDay;
    const { status, note } = readinessOf(info, endDay);
    return {
        id: r.activationId,
        name: r.name,
        startDay,
        endDay,
        time: shortTime(r.startAt),
        status,
        note,
        location: location ?? r.venueName ?? undefined,
        href: `/activations/${r.activationId}`,
    };
}

/** Remembered view per page; ?view= wins so a link can open a view. */
export function useView<T extends string>(page: string, allowed: readonly T[], fallback: T) {
    const read = (): T => {
        try {
            const fromUrl = new URLSearchParams(window.location.search).get('view') as T | null;
            if (fromUrl && allowed.includes(fromUrl)) return fromUrl;
            const saved = window.localStorage.getItem(`ps.view.${page}`) as T | null;
            if (saved && allowed.includes(saved)) return saved;
        } catch {
            /* storage blocked */
        }
        return fallback;
    };
    const [view, setView] = useState<T>(read);
    const set = (v: T) => {
        setView(v);
        try {
            window.localStorage.setItem(`ps.view.${page}`, v);
            const url = new URL(window.location.href);
            url.searchParams.delete('view');
            window.history.replaceState(window.history.state, '', url.toString());
        } catch {
            /* storage blocked */
        }
    };
    return [view, set] as const;
}
